import { z } from 'zod';

/**
 * The publish manifest, as the console validates it before sending.
 *
 * # Why this is Zod and not the JSON Schema itself
 *
 * `api/schemas/manifest/tarif_version.schema.json` is the contract, and it is enforced twice at
 * runtime: by the service before the gate runs, and by the Node contract suite. What it is NOT
 * is a form validator — it has no notion of which field a message belongs to, and the console
 * needs that to put "not a valid ERN" under the right input.
 *
 * Loading the schema into the browser and running a JSON Schema validator over it would need a
 * validator dependency the console does not have and may not add (CLAUDE.md rule 1;
 * allowed-dependencies.json scopes ajv to tools/contracts, never the console). So the shapes are
 * stated here in the stack's own validation language, and the risk that follows — a copy that
 * drifts from the schema it mirrors — is closed by a test rather than by care:
 * `tools/contracts/src/manifest-parity.test.ts` runs a corpus of manifests through BOTH this
 * schema and the real JSON Schema under AJV and fails if the two ever disagree about one.
 *
 * # Why the shapes are what they are
 *
 * Every ref is an ERN, and every ERN is service-qualified (decision S-5): the digest is computed
 * over globally unambiguous refs, so a bare `fahrrad/2026/1.2` is not an under-specified ref, it
 * is a different thing entirely and must be refused here rather than at the gate.
 */

/**
 * The closed service catalog (docs/standards/ern-grammar.md). Closed on purpose: a typo variant
 * like `tarifsevice:` must fail loudly rather than resolve to nothing at publish time.
 */
export const ERN_PATTERN =
  /^(tarifservice|workflowservice|documentservice|billingservice|dataservice|paymentservice|authservice|insuranceservice|partnerservice):[A-Za-z0-9][A-Za-z0-9_.-]*(\/[A-Za-z0-9][A-Za-z0-9_.-]*)*$/;

export const ernSchema = z.string().regex(ERN_PATTERN, { error: 'ern' });

/**
 * `versionRef` (api/schemas/version_ref.schema.json, SDD-016 §4.1): an EXACT tarif or group tarif
 * version, `tarifservice:<slug>/<generation>/<major>.<minor>`, no leading zeros.
 */
export const VERSION_REF_PATTERN =
  /^tarifservice:[a-z0-9][a-z0-9_.-]*\/[a-z0-9][a-z0-9_.-]*\/[1-9][0-9]*\.(0|[1-9][0-9]*)$/;

/** `versionRef#/$defs/floating`: additionally `<slug>/<generation>/<major>` (D-V5, a DRAFT group child pin). */
export const VERSION_REF_FLOATING_PATTERN =
  /^tarifservice:[a-z0-9][a-z0-9_.-]*\/[a-z0-9][a-z0-9_.-]*\/[1-9][0-9]*(\.(0|[1-9][0-9]*))?$/;

export const versionRefSchema = z.string().regex(VERSION_REF_PATTERN, { error: 'versionRef' });
export const versionRefFloatingSchema = z
  .string()
  .regex(VERSION_REF_FLOATING_PATTERN, { error: 'versionRef' });

/** Asset-map keys are `^[a-z0-9_]+$` — the schema's `propertyNames`, not a convention. */
export const ERN_MAP_KEY_PATTERN = /^[a-z0-9_]+$/;

const ernMapSchema = z.record(z.string().regex(ERN_MAP_KEY_PATTERN, { error: 'key' }), ernSchema);

export const releaseTypes = ['PATCH', 'GENERATION_INITIAL', 'VARIANT'] as const;
export type ReleaseType = (typeof releaseTypes)[number];

/**
 * A PMM PartnerType SLUG (SDD-014 PS-9): the value a role's `partnerTypes` names. Structurally a
 * lowercase slug — mirrors the `pattern` of `$defs/partnerRole.partnerTypes.items` in
 * api/schemas/manifest/tarif_version.schema.json — and WHICH slugs exist is the Partner domain's to
 * say: the gate vouches every value against the tenant's live type catalog (`partner_roles.types`
 * is a REF conflict since SDD-014, like `partner_roles.ref`), and the wizard offers the catalog
 * (`GET /v1/partner-types`). A made-up slug is therefore structurally VALID here and gate-refused,
 * exactly the SDD-011 pattern for tags.
 */
export const PARTNER_TYPE_SLUG_PATTERN = /^[a-z][a-z0-9_-]*$/;
export const partnerTypeSlugSchema = z
  .string()
  .regex(PARTNER_TYPE_SLUG_PATTERN, { error: 'pattern' });
export type PartnerType = string;

/**
 * The two PartnerTypes every tenant of this platform seeds (fixtures/golden/partnerservice/), kept
 * as the offline DEFAULT a form may show before the live catalog has answered — never as the set
 * of allowed values, which is the catalog's (PS-9). Spelled the platform's way; the adapter
 * translates the Partner service's `organization` at the seam (STOP-3).
 */
export const partnerTypes = ['person', 'organisation'] as const;

/**
 * One partner-role rule of a version (SDD-011 P-2): which role — a PMM Tag named as
 * `partnerservice:tag/<code>` — how many partners may fill it, and optionally which PartnerType
 * they must be. The cardinality vocabulary is the insured object's, so a configurator learns one
 * pattern. Mirrors `$defs/partnerRole` in api/schemas/manifest/tarif_version.schema.json.
 *
 * What is NOT here is the gate's business: the policyholder role with `minCount: 1, maxCount: 1`
 * (P-6), no duplicate role, `isRequired` agreeing with `minCount`, `isMulti` with `maxCount`. A
 * manifest without the policyholder is structurally valid and refused by the gate, by name.
 */
export const partnerRoleSchema = z
  .object({
    roleErn: ernSchema,
    name: z.string().min(1, { error: 'required' }),
    isRequired: z.boolean(),
    isMulti: z.boolean(),
    minCount: z.int().min(0),
    maxCount: z.int().min(1).optional(),
    partnerTypes: z.array(partnerTypeSlugSchema).min(1).optional(),
  })
  .strict()
  .refine(
    (rule) =>
      rule.partnerTypes === undefined ||
      new Set(rule.partnerTypes).size === rule.partnerTypes.length,
    { error: 'unique', path: ['partnerTypes'] },
  );

export type PartnerRole = z.infer<typeof partnerRoleSchema>;

const partnerRolesSchema = z.array(partnerRoleSchema).min(1, { error: 'required' });

/**
 * `refs` requires `insuranceCore`, names the two optional owned pins the meta-schema declares —
 * `tarifDeckung` (SSOT I-5) and `leadLifecycle` (SDD-013 §6.1) — and accepts further ERNs
 * alongside them. `catchall` rather than `strict`: the schema's `additionalProperties` is the ERN
 * schema, so an unknown ref key is allowed but an unknown ref VALUE is not.
 */
const refsSchema = z
  .object({
    insuranceCore: ernSchema,
    tarifDeckung: ernSchema.optional(),
    leadLifecycle: ernSchema.optional(),
    // SDD-015 B-7: the tarif's billing configuration, a PUBLISHED `billing_config` asset (total).
    billingConfig: ernSchema.optional(),
  })
  .catchall(ernSchema);

/**
 * `basedOn` is `ERN | null`, and null is not the same as absent: a PATCH declares what it is a
 * patch OF, and the gate refuses a PATCH whose baseline is null because it would have nothing to
 * compare against. The form therefore carries an explicit null rather than omitting the key.
 */
export const tarifVersionManifestSchema = z
  .object({
    description: z.string().min(1, { error: 'required' }),
    // DEPRECATED and optional since SDD-016 (D-V2): the gate numbers the version.
    releaseType: z.enum(releaseTypes).optional(),
    basedOn: z.union([versionRefSchema, z.null()]).optional(),
    refs: refsSchema,
    documents: ernMapSchema,
    workflows: ernMapSchema,
    settings: ernMapSchema,
    partnerRoles: partnerRolesSchema,
  })
  .strict();

export type TarifVersionManifest = z.infer<typeof tarifVersionManifestSchema>;

/**
 * The group manifest differs in exactly one place, and it is the place that matters: it pins an
 * EXACT physical child version and a group core (decision S-3). Stated here rather than in the
 * group feature so the two schemas that the parity test compares live side by side.
 */
const groupRefsSchema = z
  .object({
    // Exact once published; a DRAFT may float on the major (D-V5).
    childTarifVersion: versionRefFloatingSchema,
    groupCore: ernSchema,
    // SDD-015 B-7: the group tarif's own billing override (partial), resolved at read over the child's.
    billingConfig: ernSchema.optional(),
  })
  .catchall(ernSchema);

export const groupTarifVersionManifestSchema = z
  .object({
    description: z.string().min(1, { error: 'required' }),
    // DEPRECATED and optional since SDD-016 (D-V2): the gate numbers the version.
    releaseType: z.enum(releaseTypes).optional(),
    basedOn: z.union([versionRefSchema, z.null()]).optional(),
    refs: groupRefsSchema,
    documents: ernMapSchema,
    workflows: ernMapSchema,
    settings: ernMapSchema,
    partnerRoles: partnerRolesSchema,
  })
  .strict();

export type GroupTarifVersionManifest = z.infer<typeof groupTarifVersionManifestSchema>;
